import React, { useState, useRef } from 'react';
import {
  Edit3,
  Trash2,
  Plus,
  Camera,
  X,
  Check,
  AlertCircle,
} from 'lucide-react';
import QConfiguratorDrawerInventory from './QConfiguratorDrawerInventory';
import CameraCapture from './CameraCapture';
import {
  DRAWER_CONFIGS,
  calculateUsedUnits,
  getRemainingUnits,
  canAddDrawer,
  createEmptyDrawer,
} from './qconfigurator.types';

const QConfiguratorModuleView = ({
  module,
  cabinetName,
  onUpdateModule,
  onDeleteModule,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(module.name);
  const [expandedDrawerIndex, setExpandedDrawerIndex] = useState(null); // Use index instead of ID
  const [cameraDrawerIndex, setCameraDrawerIndex] = useState(null); // For camera modal - use index
  const nameInputRef = useRef(null);

  const drawers = module.drawers || [];
  const usedUnits = calculateUsedUnits(drawers);
  const remainingUnits = getRemainingUnits(drawers);
  const usagePercentage = (usedUnits / 3) * 100;

  // Handle name editing
  const handleStartEditName = () => {
    setEditedName(module.name);
    setIsEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const handleSaveName = () => {
    if (editedName.trim()) {
      onUpdateModule({ ...module, name: editedName.trim() });
    }
    setIsEditingName(false);
  };

  const handleCancelEditName = () => {
    setEditedName(module.name);
    setIsEditingName(false);
  };

  // Add drawer
  const handleAddDrawer = (size) => {
    if (!canAddDrawer(drawers, size)) return;

    const newDrawer = createEmptyDrawer(size);
    const newIndex = drawers.length; // Index of the new drawer
    onUpdateModule({
      ...module,
      drawers: [...drawers, newDrawer],
    });
    setExpandedDrawerIndex(newIndex);
  };

  // Delete drawer by index
  const handleDeleteDrawer = (index) => {
    onUpdateModule({
      ...module,
      drawers: drawers.filter((_, idx) => idx !== index),
    });
    if (expandedDrawerIndex === index) {
      setExpandedDrawerIndex(null);
    } else if (expandedDrawerIndex !== null && expandedDrawerIndex > index) {
      // Adjust expanded index if a drawer before it was deleted
      setExpandedDrawerIndex(expandedDrawerIndex - 1);
    }
  };

  // Update drawer by index
  const handleUpdateDrawer = (updatedDrawer, index) => {
    onUpdateModule({
      ...module,
      drawers: drawers.map((d, idx) =>
        idx === index ? updatedDrawer : d
      ),
    });
  };

  // Handle drawer image from camera
  const handleDrawerImageCapture = (index, imageData) => {
    const drawer = drawers[index];
    if (drawer) {
      handleUpdateDrawer({ ...drawer, image: imageData }, index);
    }
  };

  // Open camera for drawer
  const openCameraForDrawer = (index) => {
    setCameraDrawerIndex(index);
  };

  return (
    <div className="qconfig-module-view">
      {/* Module Header */}
      <div className="qconfig-module-header">
        <div className="qconfig-module-header-left">
          <div
            className="qconfig-module-header-color"
            style={{ backgroundColor: module.color }}
          />
          {isEditingName ? (
            <div className="qconfig-name-edit">
              <input
                ref={nameInputRef}
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEditName();
                }}
                className="qconfig-name-input"
              />
              <button onClick={handleSaveName} className="qconfig-name-btn save">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={handleCancelEditName} className="qconfig-name-btn cancel">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="qconfig-name-display">
              <h2>{module.name}</h2>
              <button onClick={handleStartEditName} className="qconfig-edit-name-btn">
                <Edit3 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <button onClick={onDeleteModule} className="qconfig-delete-module-btn">
          <Trash2 className="w-4 h-4" />
          <span>Delete Module</span>
        </button>
      </div>

      {/* Add Drawer Section */}
      <div className="qconfig-add-drawer-section">
        <h3 className="qconfig-section-title">Add Drawer</h3>
        <div className="qconfig-drawer-buttons">
          {['small', 'medium', 'large'].map((size) => {
            const config = DRAWER_CONFIGS[size];
            const canAdd = canAddDrawer(drawers, size);

            return (
              <button
                key={size}
                onClick={() => handleAddDrawer(size)}
                disabled={!canAdd}
                className={`qconfig-drawer-btn ${canAdd ? '' : 'disabled'}`}
              >
                <div className="qconfig-drawer-btn-icon">
                  <Plus className="w-5 h-5" />
                </div>
                <div className="qconfig-drawer-btn-info">
                  <span className="qconfig-drawer-btn-size">
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </span>
                  <span className="qconfig-drawer-btn-units">
                    {config.units} unit{config.units !== 1 ? 's' : ''}
                  </span>
                </div>
                {!canAdd && (
                  <div className="qconfig-drawer-btn-blocked">
                    <AlertCircle className="w-4 h-4" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Drawers List */}
      <div className="qconfig-drawers-section">
        <h3 className="qconfig-section-title">
          Drawers ({drawers.length})
        </h3>

        {drawers.length === 0 ? (
          <div className="qconfig-no-drawers">
            <p>No drawers added yet</p>
            <p className="text-sm">Use the buttons above to add drawers to this module</p>
          </div>
        ) : (
          <div className="qconfig-drawers-list">
            {drawers.map((drawer, index) => (
              <div
                key={index}
                className={`qconfig-drawer-card ${expandedDrawerIndex === index ? 'expanded' : ''}`}
              >
                {/* Drawer Header */}
                <div
                  className="qconfig-drawer-header"
                  onClick={() => setExpandedDrawerIndex(
                    expandedDrawerIndex === index ? null : index
                  )}
                >
                  <div className="qconfig-drawer-header-left">
                    <div className={`qconfig-drawer-size-badge ${drawer.size}`}>
                      {drawer.size.charAt(0).toUpperCase()}
                    </div>
                    <div className="qconfig-drawer-info">
                      <span className="qconfig-drawer-name">{drawer.name}</span>
                      <span className="qconfig-drawer-meta">
                        {drawer.units} unit{drawer.units !== 1 ? 's' : ''} &bull; {(drawer.items || []).length} item{(drawer.items || []).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="qconfig-drawer-header-right">
                    {drawer.image && (
                      <div className="qconfig-drawer-thumbnail">
                        <img src={drawer.image} alt="Drawer preview" />
                      </div>
                    )}
                    <button
                      className="qconfig-drawer-upload-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCameraForDrawer(index);
                      }}
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete "${drawer.name}"?`)) {
                          handleDeleteDrawer(index);
                        }
                      }}
                      className="qconfig-drawer-delete-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Drawer Content (Inventory) */}
                {expandedDrawerIndex === index && (
                  <div className="qconfig-drawer-content">
                    <QConfiguratorDrawerInventory
                      drawer={drawer}
                      cabinetName={cabinetName}
                      moduleName={module.name}
                      onUpdateDrawer={(updatedDrawer) => handleUpdateDrawer(updatedDrawer, index)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Camera Modal for Drawer Photos */}
      <CameraCapture
        isOpen={cameraDrawerIndex !== null}
        onClose={() => setCameraDrawerIndex(null)}
        onCapture={(imageData) => {
          if (cameraDrawerIndex !== null) {
            handleDrawerImageCapture(cameraDrawerIndex, imageData);
          }
          setCameraDrawerIndex(null);
        }}
        title="Drawer Photo"
      />
    </div>
  );
};

export default QConfiguratorModuleView;
